import type { ProviderKind } from '@hangar/shared';
import { z } from 'zod';
import { orgHeaders } from './orgSelection';
import {
  App,
  AppCreateRequest,
  BuildCreateRequest,
  BuildPriorityRequest,
  EnvImportRequest,
  EnvResolveRequest,
  EnvVarCreateRequest,
  EnvVarPatch,
  EnvVarRevealRequest,
  EnvVarVersionCreateRequest,
  Me,
  AppPatch,
  Environment,
  EnvironmentPatch,
  EnvironmentWrite,
  ProfilePatch,
  ProfileWrite,
  Project,
  ProjectCreateRequest,
  ProjectPatch,
  ProviderAccountCreateRequest,
  ProviderAccountPatch,
  ShadowSettingsPatch,
  TriggerSource,
  type BuildPriority,
  type BuildStatus,
  type Platform,
} from '@hangar/shared';
import { api, type Query } from './client';
import {
  ArtifactList,
  BitriseAppList,
  BitriseSetup,
  Build,
  BuildCheckList,
  BuildEventList,
  BuildList,
  BuildRequest,
  BuildRequestResult,
  ConfigSyncResult,
  Credential,
  EnvImportResult,
  EnvRequirements,
  EnvResolution,
  EnvSnapshot,
  EnvVar,
  EnvVarList,
  EnvVarRevealResponse,
  EnvVarVersionList,
  EnvVarWriteResult,
  LaunchForm,
  PresignedUrl,
  Profile,
  ProfileList,
  ProviderAccount,
  ProviderAccountList,
  ProviderAccountTestResult,
  ProviderTokenVerifyResult,
  ScmConnection,
  ScmConnectionList,
  ScmConnectionTestResult,
  ScmTokenDiscoverResult,
  ScmRepositoryList,
  QueueSnapshot,
  ShadowRun,
  ShadowRunList,
  ShadowSummary,
  type EnvRequirement,
} from './schemas';
import { ApiTokenCreateRequest, OidcPolicyCreateRequest, OidcPolicyPatch } from '@hangar/shared';
import { ApiTokenCreated, ApiTokenList, OidcPolicy, OidcPolicyList } from './schemas';

const ListOf =<T extends z.ZodType>(item: T) => z.object({ data: z.array(item), nextCursor: z.string().nullable() });
export const ProjectList = ListOf(Project);
export const AppList = ListOf(App);

export type ProjectCreateInput = z.input<typeof ProjectCreateRequest>;
export type AppCreateInput = z.input<typeof AppCreateRequest>;

const enc = encodeURIComponent;

/* ---------- P1 ---------- */
export const getMe = () => api('GET', '/me', { schema: Me });
export const listProjects = () => api('GET', '/projects', { schema: ProjectList, query: { limit: 200 } });
export const getProject = (projectId: string) => api('GET', `/projects/${enc(projectId)}`, { schema: Project });
export const createProject = (body: ProjectCreateInput, idempotencyKey: string) =>
  api('POST', '/projects', { schema: Project, body: ProjectCreateRequest.parse(body), idempotencyKey });
export const listApps = (projectId: string) => api('GET', '/apps', { schema: AppList, query: { projectId, limit: 200 } });
export const createApp = (body: AppCreateInput, idempotencyKey: string) =>
  api('POST', '/apps', { schema: App, body: AppCreateRequest.parse(body), idempotencyKey });
/** Merge patches with `If-Match` = the resource's `etag`; `{ archived: true }` archives (the API keeps the record). */
export type ProjectPatchInput = z.input<typeof ProjectPatch>;
export const updateProject = (projectId: string, body: ProjectPatchInput, etag?: string) =>
  api('PATCH', `/projects/${enc(projectId)}`, { schema: Project, body: ProjectPatch.parse(body), ifMatch: etag });
export type AppPatchInput = z.input<typeof AppPatch>;
export const updateApp = (appId: string, body: AppPatchInput, etag?: string) => api('PATCH', `/apps/${enc(appId)}`, { schema: App, body: AppPatch.parse(body), ifMatch: etag });

/* ---------- Environments (Project settings) ---------- */
// Reads come with the project: `Project.environments` holds every environment, archived ones included.
export type EnvironmentWriteInput = z.input<typeof EnvironmentWrite>;
export const createEnvironment = (projectId: string, body: EnvironmentWriteInput) =>
  api('POST', `/projects/${enc(projectId)}/environments`, { schema: Environment, body: EnvironmentWrite.parse(body) });
export type EnvironmentPatchInput = z.input<typeof EnvironmentPatch>;
/** Merge patch; `If-Match` = the environment's `etag`, which is its project's version (any environment write moves it). */
export const updateEnvironment = (environmentId: string, body: EnvironmentPatchInput, etag?: string) =>
  api('PATCH', `/environments/${enc(environmentId)}`, { schema: Environment, body: EnvironmentPatch.parse(body), ifMatch: etag });

/* ---------- Builds ---------- */
export interface BuildFilters {
  projectId?: string;
  app?: string;
  platform?: Platform;
  environment?: string;
  status?: BuildStatus[];
  branch?: string;
  source?: z.infer<typeof TriggerSource>;
  /** `{appSlug}#{number}`; short-circuits the other filters (at most one build). */
  ref?: string;
  createdAfter?: string;
  limit?: number;
}

export const listBuilds = (filters: BuildFilters, cursor?: string) => {
  const query: Query = { ...filters, status: filters.status && filters.status.length > 0 ? filters.status : undefined, cursor, limit: filters.limit ?? 25 };
  return api('GET', '/builds', { schema: BuildList, query });
};
export const getBuild = (buildId: string) => api('GET', `/builds/${enc(buildId)}`, { schema: Build });
export const getBuildRequest = (id: string) => api('GET', `/build-requests/${enc(id)}`, { schema: BuildRequest });
export const listBuildEvents = (buildId: string) => api('GET', `/builds/${enc(buildId)}/events`, { schema: BuildEventList, query: { limit: 200 } });
export const listBuildChecks = (buildId: string) => api('GET', `/builds/${enc(buildId)}/checks`, { schema: BuildCheckList });
export const listBuildArtifacts = (buildId: string) => api('GET', `/builds/${enc(buildId)}/artifacts`, { schema: ArtifactList });
export const getBuildEnv = (buildId: string) => api('GET', `/builds/${enc(buildId)}/env`, { schema: EnvSnapshot });
export const cancelBuild = (buildId: string, reason?: string) => api('POST', `/builds/${enc(buildId)}/cancel`, { schema: Build, body: reason ? { reason } : {} });
export const retryBuild = (buildId: string, idempotencyKey: string) => api('POST', `/builds/${enc(buildId)}/retry`, { schema: Build, idempotencyKey });
export const setBuildPriority = (buildId: string, priority: BuildPriority, reason?: string) =>
  api('POST', `/builds/${enc(buildId)}/priority`, { schema: Build, body: BuildPriorityRequest.parse({ priority, reason }) });
export const artifactDownloadUrl = (artifactId: string) => api('GET', `/artifacts/${enc(artifactId)}/download`, { schema: PresignedUrl, query: { redirect: false } });

export type BuildCreateInput = z.input<typeof BuildCreateRequest>;
export const createBuilds = (body: BuildCreateInput, idempotencyKey?: string) =>
  api('POST', '/builds', { schema: BuildRequestResult, body: BuildCreateRequest.parse(body), idempotencyKey });

export const getQueue = (projectId?: string) => api('GET', '/queue', { schema: QueueSnapshot, query: { projectId } });

/* ---------- Profiles ---------- */
export const listProfiles = (appId: string) => api('GET', `/apps/${enc(appId)}/profiles`, { schema: ProfileList, query: { limit: 100 } });
export const getLaunchForm = (profileId: string, environment: string, platform?: Platform | 'all', branch?: string) =>
  api('GET', `/profiles/${enc(profileId)}/launch-form`, { schema: LaunchForm, query: { environment, platform, branch } });
export type ProfileWriteInput = z.input<typeof ProfileWrite>;
export const createProfile = (appId: string, body: ProfileWriteInput, idempotencyKey: string) =>
  api('POST', `/apps/${enc(appId)}/profiles`, { schema: Profile, body: ProfileWrite.parse(body), idempotencyKey });
export type ProfilePatchInput = z.input<typeof ProfilePatch>;
export const updateProfile = (profileId: string, body: ProfilePatchInput, etag?: string) =>
  api('PATCH', `/profiles/${enc(profileId)}`, { schema: Profile, body: ProfilePatch.parse(body), ifMatch: etag });
export const syncAppConfig = (appId: string, content: string, dryRun: boolean, idempotencyKey?: string) =>
  api('POST', `/apps/${enc(appId)}/config/sync`, { schema: ConfigSyncResult, body: { content }, query: { dryRun }, idempotencyKey });

/* ---------- Env vars ---------- */
export interface EnvVarFilters {
  projectId?: string;
  appId?: string;
  /** Environment slug, or `none` for vars not bound to an environment. */
  environment?: string;
  platform?: Platform;
  key?: string;
  includeInherited?: boolean;
}
export const listEnvVars = (filters: EnvVarFilters) => api('GET', '/env-vars', { schema: EnvVarList, query: { ...filters, limit: 200 } });
export const getEnvVar = (id: string) => api('GET', `/env-vars/${enc(id)}`, { schema: EnvVar });
export type EnvVarCreateInput = z.input<typeof EnvVarCreateRequest>;
export const createEnvVar = (body: EnvVarCreateInput, idempotencyKey: string) =>
  api('POST', '/env-vars', { schema: EnvVarWriteResult, body: EnvVarCreateRequest.parse(body), idempotencyKey });
export type EnvVarPatchInput = z.input<typeof EnvVarPatch>;
export const updateEnvVar = (id: string, body: EnvVarPatchInput, etag?: string) => api('PATCH', `/env-vars/${enc(id)}`, { schema: EnvVar, body: EnvVarPatch.parse(body), ifMatch: etag });
export const deleteEnvVar = (id: string, etag?: string) => api('DELETE', `/env-vars/${enc(id)}`, { schema: z.null(), ifMatch: etag });
export const listEnvVarVersions = (id: string) => api('GET', `/env-vars/${enc(id)}/versions`, { schema: EnvVarVersionList, query: { limit: 50 } });
export type EnvVarVersionInput = z.input<typeof EnvVarVersionCreateRequest>;
export const createEnvVarVersion = (id: string, body: EnvVarVersionInput, idempotencyKey: string) =>
  api('POST', `/env-vars/${enc(id)}/versions`, { schema: EnvVarWriteResult, body: EnvVarVersionCreateRequest.parse(body), idempotencyKey });
export const revealEnvVar = (id: string, reason: string, versionId?: string) =>
  api('POST', `/env-vars/${enc(id)}/reveal`, { schema: EnvVarRevealResponse, body: EnvVarRevealRequest.parse({ reason, versionId }) });
export type EnvImportInput = z.input<typeof EnvImportRequest>;
export const importEnvVars = (body: EnvImportInput, idempotencyKey?: string) =>
  api('POST', '/env-vars/import', { schema: EnvImportResult, body: EnvImportRequest.parse(body), idempotencyKey });
export type EnvResolveInput = z.input<typeof EnvResolveRequest>;
export const resolveAppEnv = (appId: string, body: EnvResolveInput) => api('POST', `/apps/${enc(appId)}/env/resolve`, { schema: EnvResolution, body: EnvResolveRequest.parse(body) });
export const getEnvRequirements = (appId: string) => api('GET', `/apps/${enc(appId)}/env-requirements`, { schema: EnvRequirements });
export const replaceEnvRequirements = (appId: string, requirements: EnvRequirement[], etag?: string) =>
  api('PUT', `/apps/${enc(appId)}/env-requirements`, { schema: EnvRequirements, body: { requirements }, ifMatch: etag });

/* ---------- Provider accounts ---------- */
export const listProviderAccounts = () => api('GET', '/provider-accounts', { schema: ProviderAccountList, query: { limit: 100 } });
export type ProviderAccountCreateInput = z.input<typeof ProviderAccountCreateRequest>;
export const createProviderAccount = (body: ProviderAccountCreateInput, idempotencyKey: string) =>
  api('POST', '/provider-accounts', { schema: ProviderAccount, body: ProviderAccountCreateRequest.parse(body), idempotencyKey });
export type ProviderAccountPatchInput = z.input<typeof ProviderAccountPatch>;
export const updateProviderAccount = (id: string, body: ProviderAccountPatchInput, etag?: string) =>
  api('PATCH', `/provider-accounts/${enc(id)}`, { schema: ProviderAccount, body: ProviderAccountPatch.parse(body), ifMatch: etag });
/** Checks an Expo or Bitrise token and lists what it reaches, before it is stored (operators only); nothing is saved. */
export const verifyProviderToken = (body: { kind: ProviderKind; token: string; externalAccount?: string }) =>
  api('POST', '/provider-accounts/verify', { schema: ProviderTokenVerifyResult, body });
export const testProviderAccount = (id: string, apply = false) => api('POST', `/provider-accounts/${enc(id)}/test`, { schema: ProviderAccountTestResult, body: { apply } });
/** The Bitrise apps of a Bitrise account's workspace (404/501 while the API does not serve the route). */
export const listBitriseApps = (accountId: string) => api('GET', `/provider-accounts/${enc(accountId)}/bitrise-apps`, { schema: BitriseAppList });

/* ---------- Source control and "Create on Bitrise" ---------- */
export const listScmConnections = () => api('GET', '/scm-connections', { schema: ScmConnectionList, query: { limit: 100 } });
export const createScmConnection = (body: { name: string; workspace: string; repository?: string; credentialId: string }, idempotencyKey?: string) =>
  api('POST', '/scm-connections', { schema: ScmConnection, body: { provider: 'bitbucket', ...body }, ...(idempotencyKey ? { idempotencyKey } : {}) });
export const updateScmConnection = (id: string, body: { name?: string; credentialId?: string }, etag?: string) =>
  api('PATCH', `/scm-connections/${enc(id)}`, { schema: ScmConnection, body, ifMatch: etag });
/** What a Bitbucket token reaches, before anyone types a workspace or repository (plan P34); nothing is saved. */
export const discoverScmToken = (token: string) => api('POST', '/scm-connections/discover', { schema: ScmTokenDiscoverResult, body: { provider: 'bitbucket', token } });
/** Checks a Bitbucket token before it is stored (plan P34); nothing is saved. */
export const verifyScmToken = (body: { workspace: string; repository?: string; token: string }) =>
  api('POST', '/scm-connections/verify', { schema: ScmConnectionTestResult, body: { provider: 'bitbucket', ...body } });
export const testScmConnection = (id: string) => api('POST', `/scm-connections/${enc(id)}/test`, { schema: ScmConnectionTestResult, body: {} });
export const listScmRepositories = (id: string, query?: string) => api('GET', `/scm-connections/${enc(id)}/repositories`, { schema: ScmRepositoryList, query: { query: query || undefined } });
export const getBitriseSetup = (appId: string) => api('GET', `/apps/${enc(appId)}/bitrise`, { schema: BitriseSetup });
export interface BitriseProvisionInput {
  scmConnectionId: string;
  repository: string;
  providerAccountId?: string;
  bitriseApp?: { mode: 'create' } | { mode: 'reuse'; slug: string };
}
export const provisionBitrise = (appId: string, body: BitriseProvisionInput) => api('POST', `/apps/${enc(appId)}/bitrise/provision`, { schema: BitriseSetup, body });
export const syncBitriseWorkflows = (appId: string) => api('POST', `/apps/${enc(appId)}/bitrise/sync`, { schema: BitriseSetup, body: {} });

/* ---------- Shadow mode (P3) ---------- */
export interface ShadowRunFilters {
  status?: 'match' | 'differs';
  source?: 'pipeline' | 'nightly';
  /** UTC day `YYYY-MM-DD` of the provider build. */
  day?: string;
  /** `YYYY-MM-DD`, that day included. */
  since?: string;
  limit?: number;
}
export const listShadowRuns = (app: string, filters: ShadowRunFilters, cursor?: string) =>
  api('GET', '/shadow-runs', { schema: ShadowRunList, query: { app, ...filters, cursor, limit: filters.limit ?? 10 } });
export const getShadowRun = (id: string) => api('GET', `/shadow-runs/${enc(id)}`, { schema: ShadowRun });
export const getAppShadow = (appId: string) => api('GET', `/apps/${enc(appId)}/shadow`, { schema: ShadowSummary });
export type ShadowSettingsPatchInput = z.input<typeof ShadowSettingsPatch>;
/** The summary's `version` is the app's lockVersion; the API sends and expects it as a weak tag (`ETag: W/"12"`). */
export const shadowEtag = (version: number) => `W/"${version}"`;
export const updateAppShadow = (appId: string, body: ShadowSettingsPatchInput, version: number) =>
  api('PATCH', `/apps/${enc(appId)}/shadow`, { schema: ShadowSummary, body: ShadowSettingsPatch.parse(body), ifMatch: shadowEtag(version) });

/**
 * A new token for an existing provider-token credential, in place (`POST /credentials/{id}/rotate`, JSON
 * `{ secrets: { token } }`): the id stays, so every provider account that uses it gets the new token. The response is
 * metadata only.
 */
export const rotateTokenCredential = (credentialId: string, token: string) => api('POST', `/credentials/${enc(credentialId)}/rotate`, { schema: Credential, body: { secrets: { token } } });

/**
 * Token credential for a provider account. The contract's `POST /credentials` is multipart (`metadata` JSON part +
 * `secrets` JSON part); the token travels once, inside `secrets.token`, and the response never carries it.
 */
export async function createTokenCredential(kind: 'eas_access_token' | 'bitrise_api_token' | 'bitbucket_access_token', name: string, token: string, idempotencyKey: string) {
  const { currentIdToken, dropSession } = await import('../auth/userManager');
  const { config } = await import('../config');
  const { ApiError } = await import('./client');
  const bearer = await currentIdToken();
  if (!bearer) {
    void dropSession('expired');
    throw new ApiError(401, 'HGR-AUTH-REQUIRED', 'Not signed in');
  }
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify({ kind, name, managedBy: 'hangar' })], { type: 'application/json' }));
  form.append('secrets', new Blob([JSON.stringify({ token })], { type: 'application/json' }));
  const res = await fetch(new URL('/v1/credentials', `${config.apiUrl}/`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${bearer}`, Accept: 'application/json, application/problem+json', 'Idempotency-Key': idempotencyKey, ...orgHeaders() },
    body: form,
    cache: 'no-store',
  });
  const json: unknown = await res.json().catch(() => null);
  if (res.status === 401) {
    void dropSession('unauthorized');
  }
  if (!res.ok) {
    const b = (typeof json === 'object' && json !== null ? json : {}) as Record<string, unknown>;
    throw new ApiError(res.status, typeof b.code === 'string' ? b.code : `HTTP-${res.status}`, typeof b.detail === 'string' ? b.detail : typeof b.title === 'string' ? b.title : `Request failed (${res.status})`, typeof b.requestId === 'string' ? b.requestId : undefined);
  }
  return Credential.parse(json);
}

/* ---------- Access: API tokens and CI trust (OIDC) ---------- */
export interface ApiTokenFilters {
  kind?: 'personal' | 'service';
  includeRevoked?: boolean;
}
export const listApiTokens = (filters: ApiTokenFilters, cursor?: string) => api('GET', '/tokens', { schema: ApiTokenList, query: { ...filters, cursor, limit: 200 } });
export type ApiTokenCreateInput = z.input<typeof ApiTokenCreateRequest>;
/**
 * The plaintext comes back once, in this response only. No Idempotency-Key on purpose: a replay must never hand the
 * same secret out twice.
 */
export const createApiToken = (body: ApiTokenCreateInput) => api('POST', '/tokens', { schema: ApiTokenCreated, body: ApiTokenCreateRequest.parse(body) });
/** Soft revoke (`revokedAt`), effective on the next request that uses the token; idempotent. */
export const revokeApiToken = (tokenId: string) => api('DELETE', `/tokens/${enc(tokenId)}`, { schema: z.null() });

export const listOidcPolicies = (cursor?: string) => api('GET', '/oidc-policies', { schema: OidcPolicyList, query: { cursor, limit: 200 } });
export type OidcPolicyCreateInput = z.input<typeof OidcPolicyCreateRequest>;
export const createOidcPolicy = (body: OidcPolicyCreateInput, idempotencyKey: string) =>
  api('POST', '/oidc-policies', { schema: OidcPolicy, body: OidcPolicyCreateRequest.parse(body), idempotencyKey });
export type OidcPolicyPatchInput = z.input<typeof OidcPolicyPatch>;
export const updateOidcPolicy = (policyId: string, body: OidcPolicyPatchInput, etag?: string) =>
  api('PATCH', `/oidc-policies/${enc(policyId)}`, { schema: OidcPolicy, body: OidcPolicyPatch.parse(body), ifMatch: etag });
/** Hard delete. Tokens already exchanged under the policy stay valid until their own expiry (≤ its TTL). */
export const deleteOidcPolicy = (policyId: string) => api('DELETE', `/oidc-policies/${enc(policyId)}`, { schema: z.null() });
